import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import DeleteModal from '@atom/components/common/DeleteModal';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@atom/components/common/dragAndDrop';
import {
  WORK_ORDER_FIELD_CREATE,
  WORK_ORDER_FIELD_DELETE,
  WORK_ORDER_FIELD_UPDATE,
  WORK_ORDER_UPDATE,
} from '@atom/graph/work';
import { useWorkValidations } from '@atom/hooks/useWorkValidations';
import { Button, Icon } from '@atom/mui';
import { AddIcon, DragIndicatorIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { DataType, FieldViewType } from '@atom/types/dataType';
import {
  WorkOrderDetailType,
  WorkOrderField,
  WorkOrderFieldCreatePayload,
  WorkOrderFieldUpdatePayload,
} from '@atom/types/work';
import {
  doesNotHaveRolePermissions,
  hasRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { addToSet, removeFromSet } from '@atom/utilities/setUtilities';
import { CustomFieldView } from '@atom/utilities/taskUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import {
  COMPLETED_STATUS_ID,
  getWorkOrderStatusType,
  IN_PROGRESS_STATUS_ID,
  IN_REVIEW_STATUS_ID,
} from '@atom/utilities/workOrderStatusUtilities';

import WorkOrderDatePicker from './WorkOrderDatePicker';
import WorkOrderEnumMultipleField from './WorkOrderEnumMultipleField';
import WorkOrderEnumMultipleFieldList from './WorkOrderEnumMultipleFieldList';
import WorkOrderEnumSingleField from './WorkOrderEnumSingleField';
import WorkOrderEnumSingleFieldList from './WorkOrderEnumSingleFieldList';
import WorkOrderFieldCreateModal from './WorkOrderFieldCreateModal';
import WorkOrderFieldMenu from './WorkOrderFieldMenu';
import WorkOrderFieldUpdateModal from './WorkOrderFieldUpdateModal';
import WorkOrderHyperlinkField from './WorkOrderHyperlinkField';
import WorkOrderNumericField from './WorkOrderNumericField';
import WorkOrderRequiredFieldsConfirmModal from './WorkOrderRequiredFieldsConfirmModal';
import WorkOrderSummary from './WorkOrderSummary';
import WorkOrderTextField from './WorkOrderTextField';

import classes from './workOrderFields.module.css';

const styles = {
  icon: {
    marginRight: '5px',
  },
  addButton: {
    marginLeft: '2rem',
  },
};

const DEBOUNCE_TIME = 1000;
const DROPPABLE_ID = 'fields';

export interface Props {
  workOrderDetail: WorkOrderDetailType;
  dispatch: any;
  WorkOrderActionTypes: any;
  refetch: () => Promise<unknown>;
}

const WorkOrderFields = ({
  workOrderDetail,
  dispatch,
  WorkOrderActionTypes,
  refetch,
}: Props) => {
  const { workValidations, resolveWorkFieldError } = useWorkValidations();

  const [selectedField, setSelectedField] = useState<WorkOrderField>(null);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [fieldsLoading, setFieldsLoading] = useState<Set<string>>(new Set());
  const [hoveredField, setHoveredField] = useState<string>();
  const [fieldOrder, setFieldOrder] = useState<string[]>(
    workOrderDetail.fieldOrder,
  );
  const [fieldsConfirmModalOpen, setFieldsConfirmModalOpen] =
    useState<boolean>(false);

  const [createWorkOrderField, { loading: loadingCreate }] = useMutation(
    WORK_ORDER_FIELD_CREATE,
  );

  const [updateField, { loading: loadingUpdate }] = useMutation(
    WORK_ORDER_FIELD_UPDATE,
  );

  const [deleteField, { loading: loadingDelete }] = useMutation(
    WORK_ORDER_FIELD_DELETE,
  );

  const [updateWorkDetail] = useMutation(WORK_ORDER_UPDATE);

  const handleEditClick = (field: WorkOrderField) => {
    setSelectedField(field);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (field: WorkOrderField) => {
    setSelectedField(field);
    setDeleteModalOpen(true);
  };

  const handleAddField = (field: WorkOrderField) => {
    dispatch({
      type: WorkOrderActionTypes.ADD_WORK_ORDER_FIELD,
      data: {
        field,
      },
    });

    setFieldOrder([...fieldOrder, field.id]);
  };

  const handleEditField = (field: WorkOrderField) => {
    dispatch({
      type: WorkOrderActionTypes.UPDATE_WORK_ORDER_FIELD,
      data: {
        field,
      },
    });
  };

  const handleEditFieldValue = (field: WorkOrderField) => {
    dispatch({
      type: WorkOrderActionTypes.UPDATE_WORK_ORDER_FIELD_VALUE,
      data: {
        field,
      },
    });
  };

  const handleChange = useCallback(
    async (fieldId: string, value: any) => {
      setFieldsLoading(loading => addToSet(loading, fieldId));
      resolveWorkFieldError(fieldId);

      try {
        const res = await updateField({
          variables: {
            input: {
              workOrderId: workOrderDetail.id,
              fieldId,
              value,
            },
          },
        });

        const field = res?.data?.workOrderFieldUpdate;

        if (field.subFieldsTruncated) {
          handleEditField(field);
        } else {
          handleEditFieldValue(field);
        }

        await refetch();
      } finally {
        setFieldsLoading(loading => removeFromSet(loading, fieldId));
      }
    },
    [workOrderDetail.id, refetch, resolveWorkFieldError],
  );

  const handleChangeDebounced = useCallback(
    debounce(handleChange, DEBOUNCE_TIME),
    [handleChange],
  );

  const handleDeleteConfirm = async () => {
    const fieldOrderIndex = R.indexOf(selectedField.id, fieldOrder);
    const newFieldOrder = R.remove(fieldOrderIndex, 1, fieldOrder);
    setFieldOrder(newFieldOrder);

    const res = await deleteField({
      variables: {
        input: {
          workOrderId: workOrderDetail.id,
          fieldId: selectedField.id,
        },
      },
    });

    if (res?.data?.workOrderFieldDelete) {
      setSelectedField(null);
      setDeleteModalOpen(false);
      dispatch({
        type: WorkOrderActionTypes.DELETE_WORK_ORDER_FIELD,
        data: {
          fieldId: selectedField.id,
        },
      });
    }
  };

  const handleCreate = async (payload: WorkOrderFieldCreatePayload) => {
    const res = await createWorkOrderField({
      variables: {
        input: {
          workOrderId: workOrderDetail.id,
          ...payload,
        },
      },
    });

    handleAddField(res?.data?.workOrderFieldCreate);
    setCreateModalOpen(false);
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    const newFieldOrder = R.move(source.index, destination.index, fieldOrder);
    setFieldOrder(newFieldOrder);

    updateWorkDetail({
      variables: {
        workOrder: {
          id: workOrderDetail.id,
          fieldOrder: newFieldOrder,
        },
      },
    });

    dispatch({
      type: WorkOrderActionTypes.UPDATE_WORK_ORDER_PROPERTY,
      data: {
        property: 'fieldOrder',
        value: newFieldOrder,
      },
    });
  };

  const handleUpdate = async (payload: WorkOrderFieldUpdatePayload) => {
    const res = await updateField({
      variables: {
        input: {
          workOrderId: workOrderDetail.id,
          ...payload,
        },
      },
    });

    handleEditField(res?.data?.workOrderFieldUpdate);
    setEditModalOpen(false);
  };

  const isWorkOrderLockedForRequiredFields =
    workOrderDetail.statusId === COMPLETED_STATUS_ID ||
    workOrderDetail.statusId === IN_REVIEW_STATUS_ID;

  const handleRequiredFieldsEdit = (field: WorkOrderField) => {
    if (
      !field.required ||
      workOrderDetail.isClosed ||
      !isWorkOrderLockedForRequiredFields
    ) {
      return;
    }

    setFieldsConfirmModalOpen(true);
  };

  const submitRequiredFieldsEdit = async () => {
    await updateWorkDetail({
      variables: {
        workOrder: {
          id: workOrderDetail.id,
          statusId: IN_PROGRESS_STATUS_ID,
        },
      },
    });

    setFieldsConfirmModalOpen(false);
    await refetch();
  };

  const showButton =
    hasRolePermissions(ROLE_SETS.INSPECTOR) &&
    !workOrderDetail.isClosed &&
    !workOrderDetail.workTemplateId;

  const getContent = (field: WorkOrderField) => {
    const isFieldEditDisabled =
      doesNotHaveRolePermissions(ROLE_SETS.INSPECTOR) ||
      workOrderDetail.isClosed ||
      (field.required && isWorkOrderLockedForRequiredFields);

    const invalidFieldIds = R.pathOr(
      new Set([]),
      ['workValidations', 'invalidWorkFields'],
      workValidations,
    );

    const props = {
      field,
      fieldsLoading,
      onChange: handleChange,
      isDisabled: isFieldEditDisabled,
      onClick: () => handleRequiredFieldsEdit(field),
      onBlur: handleChange,
      customFieldView: CustomFieldView.DETAIL,
      requiredFieldIds: invalidFieldIds,
      requiredFieldName: 'required' as keyof WorkOrderField,
    };

    const hasSubFields = !isNilOrEmpty(field.subFields);

    const isListView = field.viewAs === FieldViewType.CHECKBOX;

    const components = {
      [DataType.SUMMARY]: <WorkOrderSummary {...props} />,
      [DataType.SHORT_TEXT]: (
        <WorkOrderTextField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.LONG_TEXT]: (
        <WorkOrderTextField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.DATE]: (
        <WorkOrderDatePicker {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.DATE_TIME]: (
        <WorkOrderDatePicker {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.ENUM_SINGLE]: hasSubFields ? (
        <div className={classes.enumSingleNestedContainer}>
          <WorkOrderEnumSingleField
            {...props}
            onChange={handleChangeDebounced}
          />
        </div>
      ) : isListView ? (
        <WorkOrderEnumSingleFieldList {...props} />
      ) : (
        <WorkOrderEnumSingleField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.ENUM_MULTIPLE]: isListView ? (
        <WorkOrderEnumMultipleFieldList {...props} />
      ) : (
        <WorkOrderEnumMultipleField {...props} />
      ),
      [DataType.NUMBER]: (
        <WorkOrderNumericField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.HYPERLINK]: <WorkOrderHyperlinkField {...props} />,
    };

    const iconColor =
      hoveredField === field.id ? colors.neutral.ash : colors.neutral.white;

    return (
      <div
        className={classes.fieldDragContainer}
        onMouseEnter={() => setHoveredField(field.id)}
        onMouseDown={() => setHoveredField(field.id)}
        onMouseUp={() => setHoveredField(null)}
      >
        <Icon color={iconColor} style={styles.icon} icon={DragIndicatorIcon} />
        {components[field.dataType]}
        {showButton && (
          <WorkOrderFieldMenu
            field={field}
            isWorkTemplate={!!workOrderDetail.workTemplateId}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
          />
        )}
      </div>
    );
  };

  const isDragDisabled =
    workOrderDetail?.isClosed || !isNilOrEmpty(workOrderDetail?.workTemplateId);
  const hideSection = !showButton && isNilOrEmpty(workOrderDetail.fields);

  return (
    <>
      <div
        className={clsx(
          classes.sectionContent,
          hideSection && classes.hide,
          classes.fields,
        )}
        onMouseLeave={() => setHoveredField(null)}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={DROPPABLE_ID}>
            {fieldOrder.map((fieldId: string, index: number) => {
              const field = workOrderDetail.fields.find(
                item => item.id === fieldId,
              );

              return (
                <Draggable
                  key={`${field.title}-${index}`}
                  draggableId={field.id}
                  isDragDisabled={isDragDisabled}
                  index={index}
                >
                  {getContent(field)}
                </Draggable>
              );
            })}
          </Droppable>
        </DragDropContext>
        {showButton && (
          <Button
            color="primary"
            style={styles.addButton}
            startIcon={<Icon color={colors.brand.blue} icon={AddIcon} />}
            onClick={() => setCreateModalOpen(true)}
            data-cy="workOrderFieldAddButton"
          >
            Add Field
          </Button>
        )}
      </div>
      <WorkOrderFieldCreateModal
        fields={workOrderDetail?.fields}
        handleCreate={handleCreate}
        loadingCreate={loadingCreate}
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />
      <WorkOrderFieldUpdateModal
        fields={workOrderDetail?.fields}
        handleUpdate={handleUpdate}
        loadingUpdate={loadingUpdate}
        isTitleDisabled={!!workOrderDetail.workTemplateId}
        onClose={() => setEditModalOpen(false)}
        field={selectedField}
        open={editModalOpen}
      />
      <DeleteModal
        open={deleteModalOpen}
        loading={loadingDelete}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Field"
        content="Are you sure you want to delete this field?"
      />
      <WorkOrderRequiredFieldsConfirmModal
        open={fieldsConfirmModalOpen}
        onCancel={() => setFieldsConfirmModalOpen(false)}
        onConfirm={submitRequiredFieldsEdit}
        statusLabel={getWorkOrderStatusType(workOrderDetail.statusId)?.label}
      />
    </>
  );
};

export default WorkOrderFields;
